import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from "react";
import type { OnboardingStep, OrganizationListItem, OrganizationOnboardingRun } from "@kuroo/contracts";
import { KurooApiError, KurooClient } from "@kuroo/sdk";

const onboardingSteps: Array<{ id: OnboardingStep; title: string; description: string }> = [
  { id: "identity", title: "Identidad", description: "Nombre comercial y datos que verá el equipo." },
  { id: "regional", title: "Región", description: "Idioma, moneda y zona horaria operativa." },
  { id: "location", title: "Sedes", description: "Ubicaciones y reglas de atención." },
  { id: "services", title: "Servicios", description: "Catálogo, duración, precio y modalidad de cobro." },
  { id: "team", title: "Equipo", description: "Usuarios, roles y proveedores asignados." },
  { id: "availability", title: "Disponibilidad", description: "Agendas, excepciones y anticipación." },
  { id: "public-page", title: "Página pública", description: "Marca, perfiles y publicación del directorio." },
  { id: "test-booking", title: "Reserva de prueba", description: "Validación completa antes de activar la operación." }
];

function readableError(reason: unknown): string {
  return reason instanceof Error ? reason.message : "El onboarding no está disponible temporalmente.";
}

function textDraft(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() ? value : fallback;
}

function progressFor(run: OrganizationOnboardingRun): number {
  return Math.round((run.completedSteps.length / onboardingSteps.length) * 100);
}

export function OrganizationOnboardingPage({ client, organization, expire }: { client: KurooClient; organization?: OrganizationListItem; expire: () => void }): ReactElement {
  const [run, setRun] = useState<OrganizationOnboardingRun>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>();
  const [businessName, setBusinessName] = useState("");
  const [timezone, setTimezone] = useState("");
  const [currency, setCurrency] = useState("");

  useEffect(() => {
    let active = true;
    if (!organization) {
      setLoading(false);
      return () => { active = false; };
    }
    setLoading(true);
    setError(undefined);
    void client.organizationOnboarding(organization.id)
      .then((next) => {
        if (!active) return;
        setRun(next);
        setBusinessName(textDraft(next.draft.businessName, organization.name));
        setTimezone(textDraft(next.draft.timezone, organization.timezone));
        setCurrency(textDraft(next.draft.currency, organization.currency));
      })
      .catch((reason: unknown) => {
        if (!active) return;
        if (reason instanceof KurooApiError && reason.code === "UNAUTHENTICATED") expire();
        setError(readableError(reason));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [client, expire, organization]);

  const completed = useMemo(() => new Set(run?.completedSteps ?? []), [run?.completedSteps]);
  const allStepsReady = onboardingSteps.every((step) => completed.has(step.id));

  const persist = async (patch: Partial<Pick<OrganizationOnboardingRun, "status" | "currentStep" | "completedSteps">>) => {
    if (!run || !organization) return;
    setBusy(true);
    setError(undefined);
    setMessage(undefined);
    try {
      const next = await client.updateOrganizationOnboarding({
        currentStep: patch.currentStep ?? run.currentStep,
        completedSteps: patch.completedSteps ?? run.completedSteps,
        ...(patch.status ? { status: patch.status } : {}),
        draft: {
          ...run.draft,
          businessName: businessName.trim().slice(0, 160),
          timezone: timezone.trim().slice(0, 100),
          currency: currency.trim().toUpperCase().slice(0, 3)
        }
      }, organization.id, run.id);
      setRun(next);
      setMessage(patch.status === "completed" ? "Onboarding completado. La organización puede pasar a activación." : "Avance guardado de forma segura.");
    } catch (reason) {
      if (reason instanceof KurooApiError && reason.code === "UNAUTHENTICATED") expire();
      setError(readableError(reason));
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void persist({});
  };

  const toggleStep = (step: OnboardingStep) => {
    if (!run || step === "test-booking") return;
    const next = completed.has(step) ? run.completedSteps.filter((item) => item !== step) : [...run.completedSteps, step];
    void persist({ completedSteps: next, currentStep: step });
  };

  if (!organization) return <section className="not-found"><h2>Seleccioná una organización</h2><p>El provisioning necesita un contexto de organización activo.</p></section>;
  if (loading) return <section className="onboarding-page"><div className="loading-state">Cargando onboarding…</div></section>;
  if (error && !run) return <section className="onboarding-page"><div className="alert" role="alert">{error}</div><button className="button secondary" type="button" onClick={() => window.location.reload()}>Reintentar</button></section>;
  if (!run) return <section className="onboarding-page"><div className="empty-state"><h2>Onboarding no inicializado</h2><p>La organización todavía no tiene una ejecución reanudable. Aplicá la migración de onboarding antes de continuar.</p></div></section>;

  return <section className="onboarding-page">
    <header className="page-header onboarding-header">
      <div><p className="eyebrow">Provisioning enterprise</p><h2>Preparar {organization.name}</h2><p className="lede">Un recorrido operativo, auditable y reanudable. Cada cambio se guarda en el tenant activo.</p></div>
      <div className="onboarding-progress-summary"><strong>{progressFor(run)}%</strong><span>{run.completedSteps.length} de {onboardingSteps.length} pasos</span></div>
    </header>
    {message && <div className="toast" role="status">{message}</div>}
    {error && <div className="alert" role="alert">{error}</div>}
    <div className="onboarding-grid">
      <section className="panel onboarding-steps-panel" aria-labelledby="onboarding-steps-title">
        <div className="panel-heading"><div><p className="eyebrow">Control de avance</p><h3 id="onboarding-steps-title">Checklist de activación</h3></div><span className={`tag ${run.status}`}>{run.status}</span></div>
        <div className="onboarding-progress-track" aria-label={`Progreso ${progressFor(run)}%`}><span style={{ width: `${progressFor(run)}%` }} /></div>
        <ol className="onboarding-step-list">
          {onboardingSteps.map((step, index) => {
            const isComplete = completed.has(step.id);
            const isCurrent = run.currentStep === step.id;
            const isTest = step.id === "test-booking";
            return <li key={step.id} className={`onboarding-step ${isComplete ? "complete" : ""} ${isCurrent ? "current" : ""}`}>
              <button type="button" className="onboarding-step-toggle" onClick={() => toggleStep(step.id)} disabled={busy || isTest} aria-pressed={isComplete} aria-label={`${isComplete ? "Desmarcar" : "Completar"} ${step.title}`}>
                <span className="onboarding-step-index">{isComplete ? "✓" : index + 1}</span>
              </button>
              <span className="onboarding-step-copy"><strong>{step.title}</strong><small>{step.description}</small>{isTest && <small className="field-help">Se habilita únicamente después de validar una reserva real en el entorno de prueba.</small>}</span>
              {isCurrent && <span className="tag info">Actual</span>}
            </li>;
          })}
        </ol>
      </section>
      <section className="panel onboarding-draft-panel" aria-labelledby="onboarding-draft-title">
        <div className="panel-heading"><div><p className="eyebrow">Borrador seguro</p><h3 id="onboarding-draft-title">Datos operativos</h3></div><span className="subtle">Sin credenciales</span></div>
        <p className="muted">Guardamos únicamente configuración no sensible para poder retomar el proceso. Secretos, tokens y contraseñas se configuran en sus superficies protegidas.</p>
        <form className="stack-form" onSubmit={saveDraft}>
          <label>Nombre visible<input value={businessName} maxLength={160} onChange={(event) => setBusinessName(event.target.value)} required /></label>
          <label>Zona horaria<input value={timezone} maxLength={100} onChange={(event) => setTimezone(event.target.value)} required /></label>
          <label>Moneda<input value={currency} maxLength={3} onChange={(event) => setCurrency(event.target.value.toUpperCase())} required /></label>
          <div className="button-row"><button className="button primary" type="submit" disabled={busy}>{busy ? "Guardando…" : "Guardar avance"}</button>{allStepsReady && <button className="button secondary" type="button" onClick={() => void persist({ status: "completed" })} disabled={busy}>Completar onboarding</button>}</div>
        </form>
        <div className="onboarding-guardrail"><strong>Gate de calidad</strong><span>La organización no se marca como activa desde este formulario. Primero se completan la reserva de prueba y los checks de release.</span></div>
      </section>
    </div>
  </section>;
}
